import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

const AuthContext = createContext(null);

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  // On mount, check sessionStorage for existing session
  useEffect(() => {
    const savedToken = sessionStorage.getItem('mcmon_token');
    const savedUser = sessionStorage.getItem('mcmon_user');

    if (savedToken && savedUser) {
      try {
        const parsedUser = JSON.parse(savedUser);
        setToken(savedToken);
        setUser(parsedUser);
      } catch {
        sessionStorage.removeItem('mcmon_token');
        sessionStorage.removeItem('mcmon_user');
      }
    }
    setLoading(false);
  }, []);

  const login = useCallback((newToken, newUser) => {
    setToken(newToken);
    setUser(newUser);
    sessionStorage.setItem('mcmon_token', newToken);
    sessionStorage.setItem('mcmon_user', JSON.stringify(newUser));
  }, []);

  const logout = useCallback(() => {
    setToken(null);
    setUser(null);
    sessionStorage.removeItem('mcmon_token');
    sessionStorage.removeItem('mcmon_user');
  }, []);

  const updateUser = useCallback((updatedUser) => {
    setUser(updatedUser);
    sessionStorage.setItem('mcmon_user', JSON.stringify(updatedUser));
  }, []);

  const updateToken = useCallback((newToken) => {
    setToken(newToken);
    sessionStorage.setItem('mcmon_token', newToken);
  }, []);

  const value = {
    user,
    token,
    loading,
    isAuthenticated: !!token,
    isGuest: user?.role === 'guest',
    isPlayer: user?.role === 'player',
    isAdmin: user?.role === 'admin',
    login,
    logout,
    updateUser,
    updateToken,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
